/**
 * Typed client for the Status Worker.
 *
 * Everything goes through `request`, which unwraps the `{ error: { code,
 * message } }` envelope and throws an ApiError carrying the machine-readable
 * code — the same contract website-main and website-cloud use, so error
 * handling reads the same across all three.
 *
 * **Every call here is anonymous, and there is no other kind.** There used to
 * be a second export, `dev`, holding everything behind the developer gate, kept
 * deliberately apart so an authenticated call could not end up on the anonymous
 * path by accident. That whole half is `internal.mauvely.com` now, so the
 * separation is no longer a discipline this file has to maintain — it is a
 * property of the deployment. Nothing below sends a credential because there is
 * no credential on this origin to send.
 */

import type { MonitorState, Overall } from '@shared/status';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
    readonly requestId?: string
  ) {
    super(message);
  }

  /**
   * True for an unhandled exception in the Worker, as opposed to a refusal it
   * meant to make.
   *
   * Keyed on the *code*, not the status. This used to be `status >= 500 ||
   * code === 'internal'`, which swept in every deliberate 503 — `not_configured`
   * and `migrations_pending` both — and appended "if it keeps happening, quote
   * reference …" to messages that already said exactly what to do. Offering a
   * support reference for a problem the server has just diagnosed is worse than
   * saying nothing.
   */
  get isInternal(): boolean {
    return this.code === 'internal' || (this.status >= 500 && this.code === 'unknown');
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      headers: { 'content-type': 'application/json', ...(init.headers ?? {}) }
    });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach the server. Check your connection.');
  }

  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    const envelope = payload as { error?: Record<string, unknown>; requestId?: string } | null;
    const err = envelope?.error;
    throw new ApiError(
      res.status,
      (err?.code as string) ?? 'unknown',
      (err?.message as string) ?? 'Something went wrong.',
      err ?? {},
      typeof envelope?.requestId === 'string' ? envelope.requestId : undefined
    );
  }

  return payload as T;
}

/** The only verb this site needs besides GET. There were `patch` and `del`
 *  helpers here too, for the developer area's edits; nothing on this origin
 *  edits anything any more. The three POSTs left are all subscription steps. */
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

/** The sentence to put in front of someone, given a thrown error. Used by every
 *  page's catch block so a failure reads the same wherever it happens. */
export function errorText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  if (!err.isInternal) return err.message;
  return err.requestId
    ? `${err.message} If it keeps happening, quote reference ${err.requestId}.`
    : err.message;
}

// ── wire types ──────────────────────────────────────────────────────────────

export interface DayWire {
  day: string;
  up: number;
  down: number;
  maintenance: number;
  avgLatencyMs: number | null;
}

export interface UptimeWire {
  day: number | null;
  week: number | null;
  month: number | null;
  quarter: number | null;
}

export interface PublicMonitorWire {
  id: string;
  name: string;
  description: string;
  url: string | null;
  state: MonitorState;
  stateSince: number | null;
  overridden: boolean;
  overrideNote: string | null;
  uptime: UptimeWire;
  days: DayWire[];
}

export interface IncidentUpdateWire {
  id: string;
  state: string;
  body: string;
  createdAt: number;
}

export interface IncidentWire {
  id: string;
  kind: string;
  title: string;
  impact: string;
  state: string;
  startedAt: number;
  endsAt: number | null;
  resolvedAt: number | null;
  services: { id: string; name: string }[];
  updates: IncidentUpdateWire[];
}

export interface SummaryWire {
  overall: Overall;
  updatedAt: number;
  historyDays: number;
  page: { title: string; tagline: string; homeUrl: string };
  monitors: PublicMonitorWire[];
  active: IncidentWire[];
  recent: IncidentWire[];
}

export interface LatencyWire {
  monitorId: string;
  hours: number;
  bucketSeconds: number;
  points: { at: number; avgMs: number; maxMs: number; checks: number }[];
}

// ── the client ──────────────────────────────────────────────────────────────

export const api = {
  config: () => request<{ email: boolean; historyDays: number }>('/api/status/config'),
  summary: () => request<SummaryWire>('/api/status/summary'),
  incidents: (before?: number) =>
    request<{ incidents: IncidentWire[]; nextCursor: number | null }>(
      `/api/status/incidents${before ? `?before=${before}` : ''}`
    ),
  incident: (id: string) =>
    request<{ incident: IncidentWire }>(`/api/status/incidents/${encodeURIComponent(id)}`),
  latency: (id: string, hours: 24 | 168) =>
    request<LatencyWire>(`/api/status/monitors/${encodeURIComponent(id)}/latency?hours=${hours}`),

  subscribe: (email: string) => post<{ ok: true; pending: true }>('/api/status/subscribe', { email }),
  confirm: (token: string) =>
    post<{ ok: true; email: string }>('/api/status/subscribe/confirm', { token }),
  unsubscribe: (token: string) => post<{ ok: true }>('/api/status/unsubscribe', { token })
};
